import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import ArchiveDialogIcon from "@entrilia/shared/icons/ArchiveDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { Metric } from "../../../../../api/types/portfolioMonitoringTypes";

interface Props {
  metric: Metric;
  onClose: () => void;
  onDeactivated: (metric: Metric) => void;
}

const updateMetric = withErrorHandling(adminApi.updateMetric);

const DeactivateMetricDialog = ({ metric, onClose, onDeactivated }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [saving, setSaving] = useState(false);

  const handleDeactivate = async () => {
    setSaving(true);
    const [updatedMetric, error] = await updateMetric(metric.id, { inactive: true });
    setSaving(false);

    if (error) {
      logError(error, "[DeactivateMetricDialog] updateMetric");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Metric deactivated successfully");
    onDeactivated(updatedMetric);
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Deactivate Metric</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          <ArchiveDialogIcon sx={{ width: 80, height: 80 }} />
          <Stack spacing={1} sx={{ alignItems: "center" }}>
            <Typography variant="h6" sx={{ textAlign: "center" }}>
              Deactivate metric &apos;{metric.name}&apos;?
            </Typography>
            <Typography color="textSecondary" sx={{ textAlign: "center" }}>
              Once deactivated, the metric will no longer appear on data submissions. All previously submitted data will
              be preserved. If necessary, the metric can be reactivated for future use.
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button variant="outlined" color="error" loading={saving} onClick={handleDeactivate}>
          Deactivate
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DeactivateMetricDialog;
